export enum PreferredMeasurementSystem {
  Metric = 'METRIC',
  Imperial = 'IMPERIAL',
}

export interface AccessTokenBlock {
  access_token: string;
  expires_in: number;
  jti: string;
  organization: string;
  refresh_token: string;
  scope: string;
  token_type: string;
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  lastLogin: number;
  locale: string;
  companyRoleId: number;
  roleId: number;
  preferredMeasurementSystem: PreferredMeasurementSystem;
}

// User Profile Types
export type NotificationChannel = 'WA' | 'PUSH' | 'EMAIL';

export interface SendingTimePref {
  hourFrom: number;
  hourTo: number;
  weekDays: number;
}

export interface NotificationChannelPref {
  sendingTimePref: SendingTimePref;
}

export type SendingTimePrefMap = {
  [key in NotificationChannel]?: NotificationChannelPref;
};

export type IrrigationUnit = 'MM' | 'M3HA' | 'TIME';

export interface ViewPref {
  irrUnit: IrrigationUnit;
}

export interface BlockRefSystem {
  blockedIds: number[];
}

export type UserClassification = 'PLANT' | 'SNS';

export type FirstDayOfWeek = 'SUNDAY' | 'MONDAY';

export interface UserProfile {
  uid: number;
  sendingTimePrefMap: SendingTimePrefMap | null;
  viewPref: ViewPref | null;
  blockRefSystem: BlockRefSystem | null;
  profileDescription: string;
  deactivationTime: string | null;
  classification: UserClassification;
  firstDayOfWeek: FirstDayOfWeek;
}

// Notification Type Types
export type NotificationCategory =
  | 'IRRIGATION'
  | 'IMAGERY'
  | 'PLANT'
  | 'SOIL'
  | 'WEATHER'
  | 'WEATHER_INFO'
  | 'REGISTRATION'
  | 'PROMOTION'
  | 'FRUIT'
  | 'REMINDER'
  | 'AI_AGENT'
  | 'REPORT';

export type TimingStrategy =
  | 'SCHEDULED'
  | 'USER_PERMITTED_HOURS'
  | 'BURST'
  | 'DELAYED';

export type SendingDay =
  | 'FIRST_AND_FOURTH_DAY_OF_WEEK'
  | 'SUNDAY'
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY';

export type SendingFrequency = 'ONCE_A_DAY' | 'ONCE_A_WEEK' | 'ALWAYS';

export type DeliveryPolicy = 'ONLY_LAST' | 'ONLY_FIRST' | 'ALL';

export type PublishLevel = 'GROWER' | 'PLOT' | 'USER';

export type DistributionChannel = 'WA' | 'PUSH' | 'EMAIL';

export interface NotificationType {
  uid: number;
  name: string;
  category: NotificationCategory;
  timingStrategy: TimingStrategy;
  sendingHour: [number, number] | null;
  sendingDay: SendingDay | null;
  delay: number | null;
  sendingFrequency: SendingFrequency;
  deliveryPolicy: DeliveryPolicy;
  publishLevel: PublishLevel;
  supportedDistChannel: DistributionChannel[];
  active: boolean;
}

// Notification Template Types
export type ProviderName = 'INFORU' | 'GLASSIX';

export type TemplateName =
  | 'WEEKLY_WEATHER'
  | 'WEEKLY'
  | 'WEEKLY_WITHOUT_IRR'
  | 'WEEKLY_RAIN'
  | 'WEEKLY_MULTI'
  | 'STRESS'
  | 'HW_MULTI'
  | 'HW'
  | 'OVER_IRRIGATION'
  | 'UNDER_IRRIGATION'
  | 'WELCOME'
  | 'ET_EXPLAIN'
  | 'VPD_EXPLAIN'
  | 'UPDATE_SUBSCRIPTION'
  | 'LINK_DISTRIBUTE'
  | 'SATELLITE_MAP'
  | 'HW_RECOMM_LINK'
  | 'HEATWAVE_21_WITHOUT_LINK'
  | 'HEATWAVE_22_WITHOUT_LINK'
  | 'IRR_RECOMM_LINK'
  | 'IRRIGATION_REPORT_REMINDER'
  | 'GENERIC_TEMPLATE';

export type TemplateLanguage =
  | 'ARABIC'
  | 'ENGLISH'
  | 'SPANISH'
  | 'FRENCH'
  | 'HEBREW'
  | 'AZERBAIJANI';

export type TemplateSource = 'EXTERNAL' | 'INTERNAL';


export interface NotificationTemplate {
  notificationTypeId: number;
  channel: DistributionChannel;
  providerName: ProviderName;
  templateName: TemplateName;
  language: TemplateLanguage;
  templateId: string;
  templateSource: TemplateSource;
  templateText: string | null;
  imageUri: string | null;
  providerTemplateName: string | null;
}

export enum CampaignStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  COMPLETED = 'COMPLETED',
  STOPPED = 'STOPPED',
  DELETED = 'DELETED'
}

export enum CampaignCountryCodes {
  Mexico = 'Mexico'
}

export interface Campaign {
  agentId: number;
  areas: string[];
  b2bAccountEmail: string | null;
  countryCodes: string[];
  cropTypeIds: number[];
  description: string | null;
  externalUuid: string | null;
  imageUri: string | null;
  messageText: string;
  name: string | null;
  notificationTypeIds: number[];
  status: string;
  uuid: string;
}

// Agent Types
export interface InitialPrompt {
  uid: number;
  name: string;
  prompt: string;
  dynamicPromptPlotProfiles: string | null;
}

export interface Agent {
  uid: number;
  name: string;
  explicitMarker: string | null;
  kafkaTopic: string;
  limitation: string | null;
  initialPromptId: number;
  initialPrompt: InitialPrompt;
  agentRef: string | null;
  assistantUuid: string | null;
  description: string;
}

// VPD Threshold Types
export interface VpdDetectionParams {
  MdsMin: number | null;
  VPDmax: number | null;
  MDSrate: number | null;
  PMBDelta: number | null;
  VPDNIGHTmax: number | null;
  PMBreduction: number | null;
}

export interface VpdThreshold {
  cropId: number;
  detectionParams: VpdDetectionParams;
  gbiParams: any | null;
  recommendationParams: any | null;
}

export enum UserSearchType {
  PHONE = 'PHONE',
  EMAIL = 'EMAIL',
  NAME = 'NAME',
}

export enum SchemaValidationEntityCode {
  IRRIGATION_SCHEMA = 'irrigation-recommendation',
  USER_UPDATE_SCHEMA = 'user-update',
  RECOMMENDATION_PARAMS = 'recommendation-params',
}

export enum ServiceLevel {
  MONITOR = 'MONITOR',
  GBI = 'GBI',
  BDI = 'BDI',
}

export interface SystemParams {
  server: string | null;
  password: string | null;
  keepAlive: number | null;
  accessToken: string | null;
  accessTokenKey: string | null;
  sampleRate: number | null;
  endPoint: string | null;
  apiKey: string | null;
  model: string | null;
  integrationSerial: string | null;
  unitsSystem: PreferredMeasurementSystem | null;
  vwsParams: unknown | null;
  strict: boolean | null;
  clientId: string | null;
  clientSecret: string | null;
  timezone: string | null;
  rainMeterPulse: number | null;
}

export interface ActionSystem {
  id: number;
  serial: string;
  displayName: string;
  growerId: number;
  type: string;
  active: boolean;
  params: SystemParams;
  systemInfo: unknown | null;
  growerActivationTime: number;
  lastRunEndedUTC: number | null;
}

export interface Action {
  systemId: number;
  serial: string;
  name: string;
  valves: unknown | null;
  mode: string;
  value: unknown | null;
  id: number;
}

export interface ActionsResponse {
  systems: Record<string, ActionSystem>;
  actions: Action[];
}

// Trigger Types
export interface TriggerType {
  uid: number;
  type: string;
  description: string;
  probableCause: string;
  causeAlternative: number;
}

export interface Trigger {
  uid: number;
  actionId: number;
  type: TriggerType;
  plotId: number;
  onActiveAlertState: boolean;
}
